import { HttpError } from 'react-admin';

import {
  DEFAULT_PASSWORD_POLICY,
  PasswordPolicy,
  passwordRuleCodes,
} from '../../../utilities/passwordPolicy';
import errorMessages, { errorCodes } from '../../../constants/errorMessages';

export type ResetPasswordFormFields = {
  password?: string;
  confirmPassword?: string;
}

export const PASSWORDS_DO_NOT_MATCH = 'Passwords do not match';

export const validatePasswordsMatch = (
  value: string | undefined,
  values: ResetPasswordFormFields,
): string | undefined => (value === values.password ? undefined : PASSWORDS_DO_NOT_MATCH);

export const buildResetPasswordRequest = (
  data: ResetPasswordFormFields,
  token: string | null,
) => ({ password: data.password, token });

export const RESET_PASSWORD_LINK_ERROR = errorMessages[errorCodes.RESET_PASSWORD_INVALID_TOKEN];
export const RESET_PASSWORD_GENERIC_ERROR = 'Could not set new password. Please try again later. If the problem persists, request a new set password link.';

const resetPasswordErrorCodes = [...passwordRuleCodes, errorCodes.RESET_PASSWORD_INVALID_TOKEN];
type ResetPasswordErrorCode = typeof resetPasswordErrorCodes[number];

const isResetPasswordError = (error: unknown): error is HttpError => (
  error instanceof HttpError
  && error.status === 400
  && resetPasswordErrorCodes.includes(error.message as ResetPasswordErrorCode)
);

export type ResetPasswordPolicyResult =
  | { status: 'ready'; policy: PasswordPolicy }
  | { status: 'invalidLink' };

const isPasswordPolicy = (value: unknown): value is PasswordPolicy => {
  const { minLength, requiredClasses, maxBytes } = (value ?? {}) as Partial<PasswordPolicy>;
  return [minLength, requiredClasses, maxBytes].every(Number.isInteger);
};

export const loadResetPasswordPolicy = async (
  fetchPolicy: () => Promise<unknown>,
): Promise<ResetPasswordPolicyResult> => {
  try {
    const policy = await fetchPolicy();

    if (!isPasswordPolicy(policy)) {
      return { status: 'ready', policy: DEFAULT_PASSWORD_POLICY };
    }

    const { minLength, requiredClasses, maxBytes } = policy;
    return { status: 'ready', policy: { minLength, requiredClasses, maxBytes } };
  } catch (error) {
    if (error instanceof HttpError && error.status === 400) {
      return { status: 'invalidLink' };
    }

    return { status: 'ready', policy: DEFAULT_PASSWORD_POLICY };
  }
};

export const getResetPasswordErrorMessage = (error: unknown): string => (
  isResetPasswordError(error)
    ? errorMessages[error.message as ResetPasswordErrorCode]
    : RESET_PASSWORD_GENERIC_ERROR
);
